import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { composeAddress, makeApi, type AdminAddress, type AdminProject } from '../api/client';

export function AdminProjectDetailPage(): JSX.Element {
  const { pid = '' } = useParams<{ pid: string }>();
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [project, setProject] = useState<AdminProject | null>(null);
  const [count, setCount] = useState(0);
  const [addresses, setAddresses] = useState<AdminAddress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [mainAddress, setMainAddress] = useState('');

  const [showBulk, setShowBulk] = useState(false);
  const [tpl, setTpl] = useState('{n}/7-15 Taranto Rd');
  const [range, setRange] = useState('1-70');
  const [suburb, setSuburb] = useState('Marsfield');
  const [state, setState] = useState('NSW');
  const [postcode, setPostcode] = useState('2122');
  const [reuse, setReuse] = useState(true);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedAddresses = useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const sorted = [...addresses].sort((a, b) => collator.compare(a.streetAddress, b.streetAddress));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [addresses, sortDir]);

  const load = useCallback(async () => {
    try {
      const r = await api.adminGetProject(pid);
      setProject(r.project);
      setCount(r.addressCount);
      setDisplayName(r.project.displayName);
      setMainAddress(r.project.mainAddress);
      const ar = await api.adminListProjectAddresses(pid);
      setAddresses(ar.addresses);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }, [api, pid]);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(): Promise<void> {
    setBusy(true);
    try {
      await api.adminPatchProject(pid, { displayName, mainAddress });
      setEditing(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function onBulk(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.adminBulkGenerate(pid, {
        streetAddressTemplate: tpl,
        unitRange: range,
        suburb,
        state,
        postcode,
        reuseExistingAddresses: reuse,
      });
      alert(`Created ${r.created}, reused ${r.reused}, total ${r.total}`);
      setShowBulk(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk failed');
    } finally {
      setBusy(false);
    }
  }

  if (!project) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/projects" className="text-sm text-red-400 hover:text-red-300 hover:underline">
          &larr; Back
        </Link>
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-white">{project.displayName}</h1>
          <button className="btn-secondary" onClick={() => setEditing(!editing)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="text-xs text-gray-500">
          reference number: {project.projectReferenceNumber} · pid: {project.pid} · {count} addresses
        </div>
        {editing ? (
          <div className="space-y-2">
            <div>
              <label className="label">Display name</label>
              <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div>
              <label className="label">Main address</label>
              <input className="input" value={mainAddress} onChange={(e) => setMainAddress(e.target.value)} />
            </div>
            <button className="btn-primary" disabled={busy} onClick={onSave}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <div className="text-sm text-gray-300">Main address: {project.mainAddress}</div>
        )}
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-white">Addresses</h2>
          <button className="btn-primary" onClick={() => setShowBulk(!showBulk)}>
            {showBulk ? 'Cancel' : 'Bulk-generate'}
          </button>
        </div>
        {showBulk && (
          <form onSubmit={onBulk} className="grid gap-3 sm:grid-cols-2 border border-gray-700 rounded p-3 bg-gray-800/30">
            <div className="sm:col-span-2">
              <label className="label">Street template (use &#123;n&#125; placeholder)</label>
              <input className="input" value={tpl} onChange={(e) => setTpl(e.target.value)} />
            </div>
            <div>
              <label className="label">Unit range (e.g. 1-70)</label>
              <input className="input" value={range} onChange={(e) => setRange(e.target.value)} />
            </div>
            <div>
              <label className="label">Suburb</label>
              <input className="input" value={suburb} onChange={(e) => setSuburb(e.target.value)} />
            </div>
            <div>
              <label className="label">State</label>
              <input className="input" value={state} onChange={(e) => setState(e.target.value)} />
            </div>
            <div>
              <label className="label">Postcode</label>
              <input className="input" value={postcode} onChange={(e) => setPostcode(e.target.value)} />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 text-sm text-gray-300">
              <input type="checkbox" className="accent-red-600" checked={reuse} onChange={(e) => setReuse(e.target.checked)} />
              <span>Reuse existing canonical addresses</span>
            </div>
            <div className="sm:col-span-2">
              <button className="btn-primary" disabled={busy}>
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-400 border-b border-gray-700">
              <tr>
                <th className="p-3">
                  <button
                    type="button"
                    className="font-semibold text-gray-400 hover:text-white"
                    onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                  >
                    Address {sortDir === 'asc' ? '↑' : '↓'}
                  </button>
                </th>
                <th className="p-3">Other projects</th>
              </tr>
            </thead>
            <tbody>
              {sortedAddresses.map((a) => (
                <tr key={a.addressId} className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors">
                  <td className="p-3 text-gray-200">{composeAddress(a)}</td>
                  <td className="p-3 text-xs text-gray-500">
                    {a.projectSummary
                      .filter((p) => p.projectId !== pid)
                      .map((p) => p.projectReferenceNumber)
                      .join(', ') || '—'}
                  </td>
                </tr>
              ))}
              {addresses.length === 0 && (
                <tr>
                  <td colSpan={2} className="p-4 text-center text-gray-500">
                    No addresses linked yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}
    </div>
  );
}
